import React, { useEffect, useState } from 'react';
import {
    Box,
    Container,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    IconButton,
    Snackbar,
    Alert,
    Paper,
    CircularProgress,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Stack,
    Pagination,
    LinearProgress,
    Chip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { getContactedSayantan, deleteContactedSayantan } from '../../api/sayantanAPI';
import { getAllCallTest, createCallTest } from '../../api/inquiryAPI';

const EMPTY_FORM = {
    guest_name: '',
    guest_email: '',
    guest_country_code: '+91',
    guest_phone: '',
    guest_address: '',
    pax: '',
    kids_above_5: '',
    arrival_date: '',
    destination: '',
    guest_message: ''
};

const Sayantan = () => {
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // ── Call Test state ──
    const [callTests, setCallTests] = useState([]);
    const [callTestLoading, setCallTestLoading] = useState(false);
    const [callTestPage, setCallTestPage] = useState(1);
    const [callTestTotalPages, setCallTestTotalPages] = useState(1);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);

    const fetchCallTests = async (pageNum = 1) => {
        try {
            setCallTestLoading(true);
            const res = await getAllCallTest(pageNum, 10);
            setCallTests(Array.isArray(res.data) ? res.data : []);
            setCallTestTotalPages(res.pagination?.totalPages || 1);
        } catch (error) {
            setSnackbar({ open: true, message: 'Failed to fetch call tests', severity: 'error' });
            console.error(error);
        } finally {
            setCallTestLoading(false);
        }
    };

    const handleFormChange = (field) => (e) => setForm({ ...form, [field]: e.target.value });

    const handleCloseCreateDialog = () => {
        setCreateDialogOpen(false);
        setForm(EMPTY_FORM);
    };

    const handleCreateSubmit = async () => {
        if (!form.guest_name.trim() || !form.guest_email.trim() || !form.guest_phone.trim()) {
            setSnackbar({ open: true, message: 'Name, email and phone are required', severity: 'error' });
            return;
        }
        try {
            setSaving(true);
            await createCallTest(form);
            setSnackbar({ open: true, message: 'Query created successfully', severity: 'success' });
            handleCloseCreateDialog();
            setCallTestPage(1);
            fetchCallTests(1);
        } catch (error) {
            setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to create Query', severity: 'error' });
            console.error(error);
        } finally {
            setSaving(false);
        }
    };

    const fetchContacts = async () => {
        try {
            setLoading(true);
            const res = await getContactedSayantan();
            // Adjust based on conventional generic responses, assuming res.data or res.data.data or res.data.items
            let fetchedContacts = [];
            if (res.data && Array.isArray(res.data)) {
                fetchedContacts = res.data;
            } else if (res.data && Array.isArray(res.data.data)) {
                fetchedContacts = res.data.data;
            } else if (res.data && Array.isArray(res.data.items)) {
                fetchedContacts = res.data.items;
            } else if (Array.isArray(res)) {
                fetchedContacts = res;
            } else if (res.items && Array.isArray(res.items)) {
                fetchedContacts = res.items;
            }
            setContacts(fetchedContacts);
        } catch (error) {
            setSnackbar({ open: true, message: 'Failed to fetch contacts', severity: 'error' });
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchContacts();
        fetchCallTests(1);
    }, []);

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this Query?')) return;
        try {
            await deleteContactedSayantan(id);
            setSnackbar({ open: true, message: 'Query deleted successfully', severity: 'success' });
            fetchContacts();
        } catch (error) {
            setSnackbar({ open: true, message: 'Failed to delete Query', severity: 'error' });
            console.error(error);
        }
    };

    return (
        <Container maxWidth="lg" sx={{ pt: 4, pb: 4 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
                <Typography variant="h5" fontWeight="bold">
                    Call Test
                </Typography>
                <Button variant="contained" size="small" onClick={() => setCreateDialogOpen(true)}>
                    Create Query
                </Button>
            </Stack>

            {/* ── Call Test inquiry table ── */}
            <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden', mb: 3 }}>
                <Box sx={{ px: 3, py: 2, background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" fontWeight={700} color="#fff">Call Test Inquiries</Typography>
                    <Chip label={callTests.length} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: '#fff', fontWeight: 700, ml: 0.5 }} />
                </Box>
                {callTestLoading && <LinearProgress />}
                <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                {['Name', 'Email', 'Phone', 'Destination', 'Pax', 'Arrival Date', 'Lead Source', 'Created At'].map((h) => (
                                    <TableCell key={h} sx={{ backgroundColor: '#f8fafc', fontWeight: 700, color: '#475569' }}>{h}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {callTests.length === 0 && !callTestLoading ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 4, color: '#94a3b8' }}>No call test inquiries found.</TableCell>
                                </TableRow>
                            ) : (
                                callTests.map((c) => (
                                    <TableRow key={c._id} hover>
                                        <TableCell>{c.guest_name || 'N/A'}</TableCell>
                                        <TableCell>{c.guest_email || 'N/A'}</TableCell>
                                        <TableCell>{c.guest_phone ? `${c.guest_country_code || ''} ${c.guest_phone}` : 'N/A'}</TableCell>
                                        <TableCell>{c.destination || 'N/A'}</TableCell>
                                        <TableCell>{c.pax ?? 0}</TableCell>
                                        <TableCell>{c.arrival_date ? new Date(c.arrival_date).toLocaleDateString() : 'N/A'}</TableCell>
                                        <TableCell>{c.lead_source || 'N/A'}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                                {c.created_at ? new Date(c.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : 'N/A'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5, borderTop: '1px solid #e2e8f0' }}>
                    <Pagination
                        count={callTestTotalPages}
                        page={callTestPage}
                        onChange={(e, v) => { setCallTestPage(v); fetchCallTests(v); }}
                        color="primary"
                        size="small"
                        showFirstButton
                        showLastButton
                    />
                </Box>
            </Paper>

            {/* ── Create Query modal ── */}
            <Dialog open={createDialogOpen} onClose={handleCloseCreateDialog} fullWidth maxWidth="sm">
                <DialogTitle>Create Query</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField label="Name" size="small" fullWidth required value={form.guest_name} onChange={handleFormChange('guest_name')} />
                        <TextField label="Email" size="small" fullWidth required value={form.guest_email} onChange={handleFormChange('guest_email')} />
                        <Stack direction="row" spacing={2}>
                            <TextField label="Country Code" size="small" sx={{ width: 130 }} value={form.guest_country_code} onChange={handleFormChange('guest_country_code')} />
                            <TextField label="Phone" size="small" fullWidth required value={form.guest_phone} onChange={handleFormChange('guest_phone')} />
                        </Stack>
                        <TextField label="Address" size="small" fullWidth value={form.guest_address} onChange={handleFormChange('guest_address')} />
                        <Stack direction="row" spacing={2}>
                            <TextField label="Pax" type="number" size="small" fullWidth value={form.pax} onChange={handleFormChange('pax')} />
                            <TextField label="Kids (above 5)" type="number" size="small" fullWidth value={form.kids_above_5} onChange={handleFormChange('kids_above_5')} />
                        </Stack>
                        <TextField label="Arrival Date" type="date" size="small" fullWidth value={form.arrival_date} onChange={handleFormChange('arrival_date')} InputLabelProps={{ shrink: true }} />
                        <TextField label="Destination" size="small" fullWidth value={form.destination} onChange={handleFormChange('destination')} />
                        <TextField label="Message" size="small" fullWidth multiline minRows={3} value={form.guest_message} onChange={handleFormChange('guest_message')} />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseCreateDialog} color="inherit">Cancel</Button>
                    <Button onClick={handleCreateSubmit} variant="contained" disabled={saving}>
                        {saving ? 'Saving...' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* <Typography variant="h5" fontWeight="bold" mb={3}>
                Contacted Sayantan
            </Typography> */}

            {/*================================================================
              ================================================================
              ==============OLD TABLE FOR SAYANTAN QUERY======================
              ================================================================
              ================================================================ */}
            <TableContainer component={Paper} elevation={3}>
                {loading ? (
                    <Box display="flex" justifyContent="center" p={4}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell><b>Name</b></TableCell>
                                <TableCell><b>Email</b></TableCell>
                                <TableCell><b>Message</b></TableCell>
                                <TableCell align="right"><b>Actions</b></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {contacts.length > 0 ? (
                                contacts.map((contact) => (
                                    <TableRow key={contact._id || contact.id || Math.random()}>
                                        <TableCell>{contact.name || contact.fullName || 'N/A'}</TableCell>
                                        <TableCell>{contact.email || 'N/A'}</TableCell>
                                        <TableCell>{contact.message || contact.msg || 'N/A'}</TableCell>
                                        <TableCell align="right">
                                            <IconButton color="error" onClick={() => handleDelete(contact._id || contact.id)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        No contacts found.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default Sayantan;
